import { motion } from 'framer-motion'

const MONO = "'JetBrains Mono', 'Fira Code', Consolas, Monaco, monospace"
const SANS = "'Inter', system-ui, sans-serif"

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
})

const SISTEMA_ART = `███████ ██ ███████ ████████ ███████ ███   ███    ██
██      ██ ██         ██    ██      ████ ████   ████
███████ ██ ███████    ██    █████   ██ ███ ██  ██  ██
     ██ ██      ██    ██    ██      ██  █  ██ ████████
███████ ██ ███████    ██    ███████ ██     ██ ██    ██`

const stats = [
  { v: 'R$300M+', l: 'receita gerada e influenciada' },
  { v: 'R$40k', l: 'MRR · 18 meses · 2 pessoas' },
  { v: '60k', l: 'leads gerados' },
  { v: '47', l: 'workflows n8n em produção' },
]

const projects = [
  {
    name: 'Laxtro',
    badge: 'Projeto solo',
    type: 'SaaS · Content OS · Multi-tenant',
    status: 'em construção',
    href: 'https://laxtro.com.br/',
    desc: 'Arquitetura, código e produto — tudo feito por uma pessoa. Pipeline para agências: DNA editorial da marca → pautas → roteiros → assets → distribuição por plataforma. Cada cliente isolado. Cron mensal.',
    metric: null,
    stack: ['React', 'TypeScript', 'Supabase', 'n8n', 'Claude API'],
  },
  {
    name: 'BLIPS',
    badge: 'Eng. de Crescimento',
    type: 'Fintech B2B',
    status: 'ativo',
    href: null,
    desc: 'Fintech de locação e financiamento de equipamentos B2B. Infraestrutura de dados (ETL, PostgreSQL), fallback de formulários, fleet WordPress hardened, GEO, campanhas de mídia paga.',
    metric: 'R$350M+/ano processados',
    stack: ['Python', 'FastAPI', 'PostgreSQL', 'WordPress', 'n8n'],
  },
  {
    name: 'Stealth Agency',
    badge: 'Co-fundador',
    type: 'Agência de Performance',
    status: 'R$40k MRR',
    href: null,
    desc: 'Construída do zero em 18 meses com 2 pessoas. Tráfego pago, server-side GTM, automações de relatório e infraestrutura de conversão para PMEs brasileiras.',
    metric: '15 clientes ativos',
    stack: ['Meta Ads', 'Google Ads', 'sGTM', 'Docker', 'n8n'],
  },
]

const cases = [
  {
    tag: 'BLIPS',
    prob: 'Formulários caindo em silêncio — sem erro visível, sem alerta. Leads perdidos sem rastro.',
    build: 'Sistema de fallback em 48h: validação em múltiplas camadas, fila de reenvio e reconciliação automática.',
    result: '4.200 leads recuperados · R$200k+ em receita que seria perdida',
  },
  {
    tag: 'GTM',
    prob: 'GA4 e Meta Pixel divergindo 40% nas conversões. Decisão de mídia baseada em dado errado.',
    build: 'Server-side GTM do zero: API de Conversões, hash SHA-256 de PII, deduplicação por event_id.',
    result: 'Match rate +85% · CPL reportado convergiu com o real',
  },
  {
    tag: 'GEO',
    prob: 'LPs invisíveis para ChatGPT, Claude e Perplexity em buscas comerciais B2B.',
    build: 'llms.txt estruturado, Content-Signal no robots.txt, schema markup em 280 páginas, entidade semântica GBP.',
    result: '280 LPs indexadas nas principais IAs · citações orgânicas sem pagar mídia',
  },
]

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontFamily: MONO,
      fontSize: '0.65rem',
      color: 'var(--muted)',
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      marginBottom: '1.5rem',
    }}>{children}</div>
  )
}

function Divider() {
  return <div style={{ borderTop: '1px solid var(--border)', margin: '4rem 0' }} />
}

export default function Hero() {
  return (
    <section style={{ maxWidth: 1100, margin: '0 auto', padding: '5rem 1.5rem 3rem' }}>

      {/* ── TERMINAL ── */}
      <motion.div {...fade(0)} style={{ marginBottom: '3.5rem' }}>
        <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          {/* chrome */}
          <div style={{
            background: 'var(--bg3)',
            padding: '0 1rem',
            height: 36,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            borderBottom: '1px solid var(--border)',
          }}>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ff5f56', display: 'inline-block', flexShrink: 0 }} />
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#febc2e', display: 'inline-block', flexShrink: 0 }} />
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#27c840', display: 'inline-block', flexShrink: 0 }} />
            <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: '0.68rem', color: 'var(--muted)', userSelect: 'none' }}>
              matheus@aios — sistema
            </span>
          </div>
          {/* body */}
          <div style={{ padding: '1.5rem 1.75rem 1.75rem', background: 'var(--bg2)', overflowX: 'auto' }}>
            <pre style={{
              fontFamily: MONO,
              fontSize: 'clamp(0.45rem, 1.05vw, 0.62rem)',
              lineHeight: 1.55,
              color: 'var(--green)',
              margin: 0,
              whiteSpace: 'pre',
            }}>{SISTEMA_ART}</pre>

            <div style={{ fontFamily: MONO, fontSize: '0.82rem', marginTop: '1.5rem' }}>
              <div style={{ color: 'var(--muted)', marginBottom: '0.6rem' }}>
                <span style={{ color: 'var(--muted2)' }}>$ </span>whoami
              </div>
              <p style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text)', margin: '0 0 0.6rem', fontFamily: SANS }}>
                21 anos. Uberlândia.
              </p>
              <p style={{ color: 'var(--muted)', margin: '0 0 0.4rem', maxWidth: 660, lineHeight: 1.7 }}>
                Construí uma agência de performance do zero. R$40k MRR, 15 clientes ativos.
                Full stack de verdade: da campanha no Meta ao servidor que sustenta tudo.
              </p>
              <p style={{ color: 'var(--muted)', margin: '0 0 0.4rem', maxWidth: 660, lineHeight: 1.7 }}>
                Dev em uma empresa de R$350M no setor B2B durante o dia. Infra, segurança e dados.
              </p>
              <p style={{ color: 'var(--muted)', margin: '0 0 1.25rem', maxWidth: 660, lineHeight: 1.7 }}>
                Engenharia da Computação, UFU 4° período. Comecei a trabalhar antes de terminar o curso.
                O mercado ensinou mais rápido que qualquer grade curricular.
              </p>
              <div style={{ color: 'var(--muted)' }}>
                <span style={{ color: 'var(--muted2)' }}>$ </span>
                <span style={{ display: 'inline-block', width: 8, height: '1em', background: 'var(--muted)', verticalAlign: 'text-bottom', animation: 'blink 1.2s step-end infinite' }} />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── ESCALA ── stat bar */}
      <motion.div {...fade(0.06)} style={{ marginBottom: '4rem' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0',
          border: '1px solid var(--border)',
          borderRadius: 8,
          overflow: 'hidden',
        }}>
          {stats.map((s, i) => (
            <div key={s.v} style={{
              padding: '1.5rem 1.75rem',
              borderRight: i < stats.length - 1 ? '1px solid var(--border)' : undefined,
              borderBottom: undefined,
            }}>
              <div style={{
                fontFamily: SANS,
                fontSize: 'clamp(1.5rem, 2.8vw, 2.2rem)',
                fontWeight: 800,
                color: 'var(--text)',
                letterSpacing: '-0.03em',
                lineHeight: 1,
                marginBottom: '0.5rem',
              }}>{s.v}</div>
              <div style={{
                fontFamily: MONO,
                fontSize: '0.65rem',
                color: 'var(--muted)',
                lineHeight: 1.4,
              }}>{s.l}</div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── PROJETOS ── */}
      <motion.div {...fade(0.1)}>
        <Label>projetos</Label>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))',
          gap: '1rem',
        }}>
          {projects.map(p => (
            <div key={p.name} style={{
              border: '1px solid var(--border)',
              borderRadius: 8,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
              <div style={{
                padding: '1.25rem 1.5rem 1rem',
                borderBottom: '1px solid var(--border)',
                background: 'var(--bg2)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.3rem' }}>
                  <span style={{ fontFamily: SANS, fontSize: '1.05rem', fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.015em' }}>
                    {p.name}
                  </span>
                  <span style={{
                    fontFamily: MONO,
                    fontSize: '0.58rem',
                    color: p.status.includes('construção') ? 'var(--muted)' : 'var(--green)',
                    border: `1px solid ${p.status.includes('construção') ? 'var(--border)' : 'var(--green)'}`,
                    borderRadius: 3,
                    padding: '0.12rem 0.4rem',
                    flexShrink: 0,
                  }}>{p.status}</span>
                </div>
                <div style={{ fontFamily: MONO, fontSize: '0.62rem', color: 'var(--muted)', letterSpacing: '0.04em' }}>
                  <span style={{ color: 'var(--text)', fontWeight: 600 }}>{p.badge}</span>
                  {' · '}{p.type}
                </div>
              </div>

              <div style={{ padding: '1.25rem 1.5rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <p style={{ fontSize: '0.85rem', color: 'var(--muted)', lineHeight: 1.65, margin: 0, flex: 1 }}>{p.desc}</p>

                {p.metric && (
                  <div style={{ fontFamily: MONO, fontSize: '0.82rem', fontWeight: 700, color: 'var(--text)' }}>
                    {p.metric}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                    {p.stack.map(t => (
                      <span key={t} style={{
                        fontFamily: MONO,
                        fontSize: '0.58rem',
                        color: 'var(--muted)',
                        border: '1px solid var(--border)',
                        borderRadius: 3,
                        padding: '0.12rem 0.4rem',
                      }}>{t}</span>
                    ))}
                  </div>
                  {p.href && (
                    <a href={p.href} target="_blank" rel="noopener noreferrer"
                      style={{ fontFamily: MONO, fontSize: '0.65rem', color: 'var(--muted)', transition: 'color 0.15s', flexShrink: 0 }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--muted)'}
                    >laxtro.com.br ↗</a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      <Divider />

      {/* ── CASOS ── */}
      <motion.div {...fade(0.14)}>
        <Label>casos</Label>
        <div>
          {cases.map((c, i) => (
            <div key={c.tag} style={{
              display: 'grid',
              gridTemplateColumns: '60px 1fr',
              gap: '1.5rem',
              padding: '1.5rem 0',
              borderTop: '1px solid var(--border)',
              borderBottom: i === cases.length - 1 ? '1px solid var(--border)' : undefined,
            }}>
              <div style={{ fontFamily: MONO, fontSize: '0.62rem', fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.1em', paddingTop: '0.2rem' }}>
                {c.tag}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                <p style={{ fontSize: '0.88rem', color: 'var(--muted)', lineHeight: 1.6, margin: 0 }}>{c.prob}</p>
                <p style={{ fontFamily: MONO, fontSize: '0.76rem', color: 'var(--muted)', lineHeight: 1.6, margin: 0, borderLeft: '2px solid var(--border)', paddingLeft: '0.75rem' }}>{c.build}</p>
                <div style={{ fontFamily: MONO, fontSize: '0.82rem', fontWeight: 700, color: 'var(--text)' }}>{c.result}</div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      <Divider />

      {/* ── EM PRODUÇÃO ── */}
      <motion.div {...fade(0.18)}>
        <Label>em produção</Label>
        <div style={{ fontFamily: MONO, fontSize: '0.82rem', lineHeight: 1.95 }}>
          {[
            { tag: 'AIOS', desc: '20+ agentes Claude Code em produção, least-privilege por papel, KB + memória persistente cross-session' },
            { tag: 'N8N', desc: '47 workflows em queue mode (1 main + 2 workers + Redis) — zero downtime há 6 meses' },
            { tag: 'WP', desc: 'Fleet de 9 servidores: SSH key-only, WAF mu-plugin, scanner automatizado de 16 camadas em cron' },
            { tag: 'sGTM', desc: '3 propriedades com server-side GTM — CAPI, deduplicação, hash de PII — match rate +85%' },
            { tag: 'GEO', desc: '280 LPs indexadas em ChatGPT/Claude/Perplexity via llms.txt + Content-Signal + schema markup' },
          ].map(c => (
            <div key={c.tag} style={{ display: 'flex', gap: '1.5rem', alignItems: 'baseline' }}>
              <span style={{ color: 'var(--muted)', fontWeight: 700, fontSize: '0.62rem', letterSpacing: '0.1em', width: 44, textAlign: 'right', flexShrink: 0 }}>{c.tag}</span>
              <span style={{ color: 'var(--muted)' }}>{c.desc}</span>
            </div>
          ))}
        </div>
      </motion.div>

    </section>
  )
}
